import * as React from "react";
import {
    Edit, SimpleForm, TextInput, NumberInput, List, Datagrid, TextField,
    NumberField, DateField, Create, BooleanInput, BooleanField,
    SelectInput, FormDataConsumer, useGetList, ReferenceInput,
    Show, SimpleShowLayout,
} from 'react-admin';
import PenaltyCalculatorPreview from '../components/PenaltyCalculatorPreview';

// -----------------------------------------------------------------------------
// Penalty type field visibility rules
// -----------------------------------------------------------------------------
const FIELD_VISIBILITY = {
    overtime: ['multiplier', 'min_rate', 'max_rate', 'accrual_unit_mins', 'exclude_holiday_pay'],
    overtime_after_11pm: ['multiplier', 'min_rate', 'max_rate', 'accrual_unit_mins', 'exclude_holiday_pay'],
    early_call: ['multiplier', 'cutoff_time', 'accrual_unit_mins'],
    night_work: ['multiplier', 'start_time', 'weekly_cap_hours', 'default_resolution', 'end_of_contract_rest_day', 'suppressed_by_overtime', 'accrual_unit_mins'],
    broken_turnaround: ['multiplier', 'turnaround_hours', 'accrual_unit_mins'],
    delayed_meal: ['multiplier', 'min_rate', 'max_rate', 'trigger_hours', 'accrual_unit_mins', 'exclude_holiday_pay'],
    curtailed_meal: ['multiplier', 'min_rate', 'max_rate', 'cap_mins_swd', 'cap_mins_scwd', 'accrual_unit_mins', 'exclude_holiday_pay'],
    travel: ['multiplier', 'min_rate', 'max_rate', 'accrual_unit_mins', 'exclude_holiday_pay'],
};

const resolutionChoices = [
    { id: 'pay', name: 'Pay' },
    { id: 'rest', name: 'Compensatory Rest' },
];

// -----------------------------------------------------------------------------
// PenaltyRuleFields — renders only the fields relevant to the selected type
// -----------------------------------------------------------------------------
const PenaltyRuleFields = ({ penaltyTypes }) => (
    <FormDataConsumer>
        {({ formData }) => {
            const selectedType = penaltyTypes.find(t => t.id === formData.penalty_type_id);
            const code = selectedType ? selectedType.code : null;
            const visible = code ? (FIELD_VISIBILITY[code] || []) : [];
            const show = field => visible.includes(field);

            return (
                <>
                    {show('multiplier') && (
                        <NumberInput source="multiplier" step={0.25} min={0} label="Multiplier" />
                    )}
                    {show('min_rate') && (
                        <NumberInput source="min_rate" step={0.01} min={0} label="Min Rate (£)" />
                    )}
                    {show('max_rate') && (
                        <NumberInput source="max_rate" step={0.01} min={0} label="Max Rate (£)" />
                    )}
                    {show('exclude_holiday_pay') && (
                        <BooleanInput source="exclude_holiday_pay" label="Exclude Holiday Pay (use net hourly rate as base)" defaultValue={false} />
                    )}
                    {show('accrual_unit_mins') && (
                        <NumberInput source="accrual_unit_mins" step={1} min={1} label="Accrual Unit (mins)" />
                    )}
                    {show('cutoff_time') && (
                        <TextInput source="cutoff_time" label="Cutoff Time (HH:MM)" placeholder="07:00" />
                    )}
                    {show('start_time') && (
                        <TextInput source="start_time" label="Night Work Start Time (HH:MM)" placeholder="23:00" />
                    )}
                    {show('weekly_cap_hours') && (
                        <NumberInput source="weekly_cap_hours" step={0.5} min={0} label="Weekly Cap (hours)" />
                    )}
                    {show('default_resolution') && (
                        <SelectInput source="default_resolution" choices={resolutionChoices} label="Default Resolution" />
                    )}
                    {show('suppressed_by_overtime') && (
                        <BooleanInput source="suppressed_by_overtime" label="Suppressed by Overtime (after 11PM)" />
                    )}
                    {show('end_of_contract_rest_day') && (
                        <BooleanInput source="end_of_contract_rest_day" label="End of Contract Rest Day" />
                    )}
                    {show('turnaround_hours') && (
                        <NumberInput source="turnaround_hours" step={0.5} min={0} label="Required Turnaround (hours)" />
                    )}
                    {show('trigger_hours') && (
                        <NumberInput source="trigger_hours" step={0.5} min={0} label="Meal Trigger (hours after call)" />
                    )}
                    {show('cap_mins_swd') && (
                        <NumberInput source="cap_mins_swd" step={1} min={0} label="SWD Cap (mins)" />
                    )}
                    {show('cap_mins_scwd') && (
                        <NumberInput source="cap_mins_scwd" step={1} min={0} label="SCWD Cap (mins)" />
                    )}
                </>
            );
        }}
    </FormDataConsumer>
);

// -----------------------------------------------------------------------------
// Penalty Groups — List
// -----------------------------------------------------------------------------
const PenaltyGroupTitle = props => (
    <span>{props.record ? props.record.name : ''}</span>
);

export const PenaltyGroupsList = props => (
    <List {...props}>
        <Datagrid rowClick="edit">
            <TextField source="name" />
            <BooleanField source="is_default" label="Default" />
            <BooleanField source="applies_sixth_seventh_day_rules" label="6th/7th Day Rules" />
            <DateField source="created_at" />
        </Datagrid>
    </List>
);

export const PenaltyGroupsEdit = props => (
    <Edit {...props} title={<PenaltyGroupTitle />} undoable={false}>
        <SimpleForm redirect="list">
            <TextInput source="name" />
            <BooleanInput source="is_default" label="Default Group" />
            <BooleanInput
                source="applies_sixth_seventh_day_rules"
                label="Applies PACT/BECTU HETV 2023 6th/7th Day Rules"
                helperText="Enables automatic 6th/7th consecutive day pay (clauses 2.3/2.4) for employments in this group"
            />
        </SimpleForm>
    </Edit>
);

export const PenaltyGroupsCreate = props => (
    <Create {...props} title="New Penalty Group" undoable={false} redirect="list">
        <SimpleForm redirect="list">
            <TextInput source="name" />
            <BooleanInput source="is_default" label="Default Group" defaultValue={false} />
            <BooleanInput
                source="applies_sixth_seventh_day_rules"
                label="Applies PACT/BECTU HETV 2023 6th/7th Day Rules"
                helperText="Enables automatic 6th/7th consecutive day pay (clauses 2.3/2.4) for employments in this group"
                defaultValue={false}
            />
        </SimpleForm>
    </Create>
);

// -----------------------------------------------------------------------------
// Penalty Rules — List
// -----------------------------------------------------------------------------
export const PenaltyRulesList = props => (
    <List {...props}>
        <Datagrid rowClick="edit">
            <TextField source="penalty_type.full_name" label="Penalty Type" />
            <TextField source="penalty_group_id" label="Group" />
            <NumberField source="multiplier" />
            <NumberField source="accrual_unit_mins" label="Accrual Unit (mins)" />
            <DateField source="created_at" />
        </Datagrid>
    </List>
);

// -----------------------------------------------------------------------------
// Penalty Rules — Edit
// -----------------------------------------------------------------------------
export const PenaltyRulesEdit = props => {
    const { ids: typeIds, data: penaltyTypesData, loaded: typesLoaded } = useGetList(
        'penalty-types',
        { page: 1, perPage: 100 },
        { field: 'full_name', order: 'ASC' }
    );

    const { ids: groupIds, data: groupsData, loaded: groupsLoaded } = useGetList(
        'penalty-groups',
        { page: 1, perPage: 100 },
        { field: 'name', order: 'ASC' }
    );

    const penaltyTypes = typesLoaded ? typeIds.map(id => penaltyTypesData[id]) : [];
    const typeChoices = penaltyTypes.map(t => ({ id: t.id, name: t.full_name }));
    const groupChoices = groupsLoaded ? groupIds.map(id => ({ id, name: groupsData[id].name })) : [];

    return (
        <Edit {...props} undoable={false}>
            <SimpleForm redirect="list">
                <SelectInput source="penalty_group_id" choices={groupChoices} label="Penalty Group" />
                <SelectInput source="penalty_type_id" choices={typeChoices} label="Penalty Type" />
                <PenaltyRuleFields penaltyTypes={penaltyTypes} />
            </SimpleForm>
        </Edit>
    );
};

// -----------------------------------------------------------------------------
// Penalty Rules — Create
// -----------------------------------------------------------------------------
export const PenaltyRulesCreate = props => {
    const { ids: typeIds, data: penaltyTypesData, loaded: typesLoaded } = useGetList(
        'penalty-types',
        { page: 1, perPage: 100 },
        { field: 'full_name', order: 'ASC' }
    );

    const { ids: groupIds, data: groupsData, loaded: groupsLoaded } = useGetList(
        'penalty-groups',
        { page: 1, perPage: 100 },
        { field: 'name', order: 'ASC' }
    );

    const penaltyTypes = typesLoaded ? typeIds.map(id => penaltyTypesData[id]) : [];
    const typeChoices = penaltyTypes.map(t => ({ id: t.id, name: t.full_name }));
    const groupChoices = groupsLoaded ? groupIds.map(id => ({ id, name: groupsData[id].name })) : [];

    return (
        <Create {...props} undoable={false} redirect="list">
            <SimpleForm redirect="list">
                <SelectInput source="penalty_group_id" choices={groupChoices} label="Penalty Group" />
                <SelectInput source="penalty_type_id" choices={typeChoices} label="Penalty Type" />
                <PenaltyRuleFields penaltyTypes={penaltyTypes} />
            </SimpleForm>
        </Create>
    );

};

export const PenaltyGroupsShow = props => (
    <Show {...props}>
        <SimpleShowLayout>
            <TextField source="name" />
            <BooleanField source="is_default" label="Default Group" />
            <BooleanField source="applies_sixth_seventh_day_rules" label="Applies PACT/BECTU HETV 2023 6th/7th Day Rules" />
            <PenaltyCalculatorPreview />
        </SimpleShowLayout>
    </Show>
);